import {
  authResponseSchema,
  currentUserSchema,
  type CurrentUser,
} from "@opendialysis/procare-api-schemas";

import { queryClient } from "../../app/queryClient";
import { applyPreferredLanguage } from "../../lib/uiLanguage";
import { apiClient, setForcedLogoutHandler } from "../../lib/apiClient";
import { API } from "../../constants/apiRoutes";
import {
  endSession as endSessionStore,
  setAnonymous,
  setCurrentUser,
  setTokens,
} from "../../lib/session";

/**
 * The authoritative identity call, made once on sign-in and once per bootstrap.
 *
 * It is also the single place the saved UI language is applied, for the same
 * reason it is the single place identity is read: both paths into an
 * authenticated app go through here, so the language is right before the first
 * screen renders and there is no second rule to keep in step.
 */
async function fetchMe(): Promise<CurrentUser> {
  const { data } = await apiClient.get(API.auth.me);
  // currentUserSchema, NOT userResponseSchema: zod strips undeclared keys, so
  // the narrower schema would silently drop `capabilities` and every gated
  // control in the app would read as forbidden.
  const user = currentUserSchema.parse(data);
  applyPreferredLanguage(user.preferredLanguage);
  return user;
}

export async function signIn(email: string, password: string) {
  const { data } = await apiClient.post(
    API.auth.login,
    { email, password },
    // No bearer on login: a stale token would be attached for nothing, and on
    // the /activate flow it would be actively wrong.
    { skipAuth: true },
  );
  const parsed = authResponseSchema.parse(data);
  setTokens(parsed.token, parsed.tokenExpireAt);
  // The login response carries no user object, only a userId — /auth/me is the
  // authoritative identity, including the server-computed capabilities.
  setCurrentUser(await fetchMe());
}

export async function signOut() {
  try {
    await apiClient.post(API.auth.logout);
  } catch {
    // A failed logout must still clear the client: the access token expires in
    // five minutes and leaving the UI signed in is worse than an orphaned
    // server-side refresh row.
  }
  endSession();
}

/** Clears every trace of the session, including cached clinical data. */
export function endSession(): boolean {
  return endSessionStore(() => queryClient.clear());
}

/**
 * Restores a session from the httpOnly refresh cookie.
 *
 * MEMOISED AT MODULE SCOPE, deliberately. React 19 StrictMode double-invokes
 * effects in development; two bootstrap refreshes hit the server's
 * delete-on-use rotation and sign the developer out at random, reproducible
 * only in dev. Memoising here makes both invocations share one request — the
 * same trick the response interceptor uses for concurrent 401s.
 */
let bootstrapPromise: Promise<CurrentUser | null> | null = null;

export function bootstrapSession(): Promise<CurrentUser | null> {
  bootstrapPromise ??= (async () => {
    try {
      const { data } = await apiClient.post(API.auth.refresh, undefined, {
        skipAuth: true,
      });
      const parsed = authResponseSchema.parse(data);
      setTokens(parsed.token, parsed.tokenExpireAt);
      const user = await fetchMe();
      setCurrentUser(user);
      return user;
    } catch {
      // No cookie, or an expired/rotated one: an ordinary signed-out visitor.
      setAnonymous();
      return null;
    }
  })();
  return bootstrapPromise;
}

/** Test-only. */
export function resetBootstrapForTests() {
  bootstrapPromise = null;
}

// The interceptor cannot import i18n or the router without cycles, so the
// forced-logout path is injected here instead.
setForcedLogoutHandler(() => {
  if (endSession()) {
    window.dispatchEvent(new CustomEvent("procare:session-expired"));
  }
});
